import { useEffect, useMemo, useRef, useState } from "react";
import { formatProfileList } from "../lib/profileValues.mjs";


const profileSaveError = (error) => {
  const fallback = "Unable to save candidate profile. Check the field values and retry.";
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (!Array.isArray(detail)) return fallback;
  const messages = detail.filter((item) => typeof item?.msg === "string").map((item) => {
    const field = Array.isArray(item.loc)
      ? item.loc.filter((part) => part !== "body").map((part) => String(part).replace(/_/g, " ")).join(" · ")
      : "";
    return field ? `${field}: ${item.msg}` : item.msg;
  });
  return messages.length ? messages.join("; ") : fallback;
};

const emptyForm = {
  date_of_birth: "",
  gender: "",
  phone: "",
  whatsapp: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  county: "",
  postcode: "",
  country: "",
  nationality: "",
  headline: "",
  summary: "",
  skills: "",
  languages: "",
  qualifications: "",
  certifications: "",
  professional_registrations: "",
  employment_history: "",
  education: "",
  preferred_occupations: "",
  preferred_countries: "",
  salary_expectation: "",
  availability: "",
  work_authorisation: "",
  relocation: "",
  requires_sponsorship: "",
};

const csv = (value) => String(value || "")
  .split(",")
  .map((item) => item.trim())
  .filter(Boolean);

const lines = (value) => String(value || "")
  .split("\n")
  .map((item) => item.trim())
  .filter(Boolean);

const joinLines = (value) => formatProfileList(value, "\n");

const Field = ({ label, children, required = false }) => (
  <label className="block text-sm">
    <span className="font-medium text-slate-700">
      {label}{required && <span className="text-red-600"> *</span>}
    </span>
    {children}
  </label>
);

export default function AdminStructuredProfile({ candidate, onSave, busy }) {
  const profile = useMemo(() => {
    const stored = candidate.profile || {};
    return { ...stored, contact_details: { ...(stored.contact_details || {}), phone: candidate.phone || "", whatsapp: candidate.whatsapp || "" },
      address: { ...(stored.address || {}), city: candidate.city || "", country: candidate.country || "" } };
  }, [candidate]);
  const loadedProfile = useRef("");
  const [baseline, setBaseline] = useState(emptyForm);
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const signature = `${candidate.id}:${JSON.stringify(profile)}`;
    if (loadedProfile.current === signature) return;
    loadedProfile.current = signature;
    const personal = profile?.personal_details || {};
    const contact = profile?.contact_details || {};
    const address = profile?.address || {};

    const initial = {
      ...emptyForm,
      date_of_birth: personal.date_of_birth || "",
      gender: personal.gender || "",
      phone: contact.phone || "",
      whatsapp: contact.whatsapp || "",
      address_line_1: address.address_line_1 || "",
      address_line_2: address.address_line_2 || "",
      city: address.city || "",
      county: address.county || "",
      postcode: address.postcode || "",
      country: address.country || "",
      nationality: profile?.nationality || "",
      headline: profile?.headline || "",
      summary: profile?.summary || "",
      skills: formatProfileList(profile?.skills, ", "),
      languages: formatProfileList(profile?.languages, ", "),
      qualifications: joinLines(profile?.qualifications),
      certifications: joinLines(profile?.certifications),
      professional_registrations: joinLines(profile?.professional_registrations),
      employment_history: joinLines(profile?.employment_history),
      education: joinLines(profile?.education),
      preferred_occupations: formatProfileList(profile?.preferred_occupations, ", "),
      preferred_countries: formatProfileList(profile?.preferred_countries, ", "),
      salary_expectation: profile?.salary_expectation || "",
      availability: profile?.availability || "",
      work_authorisation: profile?.work_authorisation || "",
      relocation: profile?.relocation === true ? "yes" : profile?.relocation === false ? "no" : "",
      requires_sponsorship: profile?.requires_sponsorship === true ? "yes" : profile?.requires_sponsorship === false ? "no" : "",
    };
    setForm(initial); setBaseline(initial);
  }, [profile, candidate.id]);

  const requiredValues = useMemo(() => [
    form.phone,
    form.city,
    form.country,
    form.nationality,
    form.headline,
    form.summary,
    form.skills,
    form.languages,
    form.qualifications,
    form.employment_history,
    form.education,
    form.availability,
    form.work_authorisation,
  ], [form]);

  const completedRequired = requiredValues.filter(
    (value) => String(value || "").trim()
  ).length;

  const completion = Math.round(
    (completedRequired / requiredValues.length) * 100
  );

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setMessage("");
  };

  const save = async () => {
    if (saving || busy) return;
    const lists = new Set(["skills", "languages", "preferred_occupations", "preferred_countries"]);
    const lineLists = new Set(["qualifications", "certifications", "professional_registrations", "employment_history", "education"]);
    const changes = Object.fromEntries(Object.entries(form).filter(([key, value]) => value !== baseline[key]).map(([key, value]) =>
      [key, lists.has(key) ? csv(value) : lineLists.has(key) ? lines(value) :
        key === "relocation" || key === "requires_sponsorship" ? (value === "" ? null : value === "yes") : value]));
    if (!Object.keys(changes).length) { setMessage("No profile changes to save."); return; }
    setSaving(true); setMessage("");
    try {
      const response = await onSave(changes);
      if (response) { setBaseline(form); setMessage(response.warning || "Candidate profile saved."); }
    } catch (error) {
      setMessage(profileSaveError(error));
    } finally { setSaving(false); }
  };

  return (
    <section
      className="card p-6 lg:col-span-3"
      data-testid="staff-profile-editor"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Edit complete candidate profile</h2>
          <p className="mt-1 text-sm text-slate-600">
            Review and update the candidate’s personal, address and professional details.
            Only changed fields are saved.
          </p>
        </div>
        <div className="min-w-48">
          <div className="flex justify-between text-xs text-slate-600">
            <span>Profile completion</span>
            <strong data-testid="profile-completion">{completion}%</strong>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full bg-[#1d4ed8]"
              style={{ width: `${completion}%` }}
            />
          </div>
        </div>
      </div>

      {completion < 100 && (
        <p
          className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="profile-missing-guidance"
        >
          Complete the fields marked with an asterisk before application
          processing. You can save partial information and return later.
        </p>
      )}

      <fieldset disabled={saving || busy}>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Phone number" required>
          <input
            className="field mt-1"
            autoComplete="tel"
            value={form.phone}
            onChange={(event) => update("phone", event.target.value)}
          />
        </Field>

        <Field label="WhatsApp number">
          <input
            className="field mt-1"
            value={form.whatsapp}
            onChange={(event) => update("whatsapp", event.target.value)}
          />
        </Field>

        <Field label="Date of birth">
          <input
            className="field mt-1"
            type="date"
            value={form.date_of_birth}
            onChange={(event) => update("date_of_birth", event.target.value)}
          />
        </Field>

        <Field label="Gender">
          <select
            className="field mt-1"
            value={form.gender}
            onChange={(event) => update("gender", event.target.value)}
          >
            <option value="">Prefer not to specify</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="non_binary">Non-binary</option>
            <option value="self_described">Self-described</option>
          </select>
        </Field>

        <Field label="Nationality" required>
          <input
            className="field mt-1"
            autoComplete="country-name"
            value={form.nationality}
            onChange={(event) => update("nationality", event.target.value)}
          />
        </Field>

        <Field label="Country of residence" required>
          <input
            className="field mt-1"
            autoComplete="country-name"
            value={form.country}
            onChange={(event) => update("country", event.target.value)}
          />
        </Field>

        <Field label="City" required>
          <input
            className="field mt-1"
            autoComplete="address-level2"
            value={form.city}
            onChange={(event) => update("city", event.target.value)}
          />
        </Field>

        <Field label="County or state">
          <input
            className="field mt-1"
            autoComplete="address-level1"
            value={form.county}
            onChange={(event) => update("county", event.target.value)}
          />
        </Field>

        <Field label="Postcode">
          <input
            className="field mt-1"
            autoComplete="postal-code"
            value={form.postcode}
            onChange={(event) => update("postcode", event.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Professional headline" required>
          <input
            className="field mt-1"
            placeholder="Example: Healthcare assistant with three years’ experience"
            value={form.headline}
            onChange={(event) => update("headline", event.target.value)}
          />
        </Field>

        <Field label="Availability" required>
          <input
            className="field mt-1"
            placeholder="Example: Immediately or one month notice"
            value={form.availability}
            onChange={(event) => update("availability", event.target.value)}
          />
        </Field>
      </div>

      <Field label="Professional summary" required>
        <textarea
          className="field mt-1 min-h-24"
          value={form.summary}
          onChange={(event) => update("summary", event.target.value)}
        />
      </Field>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Skills — comma separated" required>
          <input
            className="field mt-1"
            data-testid="profile-skills-input"
            value={form.skills}
            onChange={(event) => update("skills", event.target.value)}
          />
        </Field>

        <Field label="Languages — comma separated" required>
          <input
            className="field mt-1"
            value={form.languages}
            onChange={(event) => update("languages", event.target.value)}
          />
        </Field>

        <Field label="Qualifications — one per line" required>
          <textarea
            className="field mt-1 min-h-24"
            value={form.qualifications}
            onChange={(event) => update("qualifications", event.target.value)}
          />
        </Field>

        <Field label="Education — one entry per line" required>
          <textarea
            className="field mt-1 min-h-24"
            value={form.education}
            onChange={(event) => update("education", event.target.value)}
          />
        </Field>

        <Field label="Employment history — one role per line" required>
          <textarea
            className="field mt-1 min-h-24"
            value={form.employment_history}
            onChange={(event) => update("employment_history", event.target.value)}
          />
        </Field>

        <Field label="Work authorisation" required>
          <textarea
            className="field mt-1 min-h-24"
            placeholder="State the countries where you can work and any restrictions"
            value={form.work_authorisation}
            onChange={(event) => update("work_authorisation", event.target.value)}
          />
        </Field>
      </div>

      <button
        type="button"
        className="btn btn-ghost mt-5"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        {expanded ? "Hide additional details" : "Add more professional details"}
      </button>

      {expanded && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Address line 1">
            <input
              className="field mt-1"
              autoComplete="address-line1"
              value={form.address_line_1}
              onChange={(event) => update("address_line_1", event.target.value)}
            />
          </Field>

          <Field label="Address line 2">
            <input
              className="field mt-1"
              autoComplete="address-line2"
              value={form.address_line_2}
              onChange={(event) => update("address_line_2", event.target.value)}
            />
          </Field>

          <Field label="Certifications — one per line">
            <textarea
              className="field mt-1 min-h-20"
              value={form.certifications}
              onChange={(event) => update("certifications", event.target.value)}
            />
          </Field>

          <Field label="Professional registrations — one per line">
            <textarea
              className="field mt-1 min-h-20"
              value={form.professional_registrations}
              onChange={(event) => update("professional_registrations", event.target.value)}
            />
          </Field>

          <Field label="Preferred occupations — comma separated">
            <input
              className="field mt-1"
              value={form.preferred_occupations}
              onChange={(event) => update("preferred_occupations", event.target.value)}
            />
          </Field>

          <Field label="Preferred countries — comma separated">
            <input
              className="field mt-1"
              value={form.preferred_countries}
              onChange={(event) => update("preferred_countries", event.target.value)}
            />
          </Field>

          <Field label="Salary expectation">
            <input
              className="field mt-1"
              value={form.salary_expectation}
              onChange={(event) => update("salary_expectation", event.target.value)}
            />
          </Field>

          <div className="space-y-3 text-sm">
            <Field label="Willing to relocate"><select className="field mt-1" value={form.relocation} onChange={(event) => update("relocation", event.target.value)}><option value="">Not recorded</option><option value="yes">Yes</option><option value="no">No</option></select></Field>
            <Field label="Requires visa sponsorship"><select className="field mt-1" value={form.requires_sponsorship} onChange={(event) => update("requires_sponsorship", event.target.value)}><option value="">Not recorded</option><option value="yes">Yes</option><option value="no">No</option></select></Field>
          </div>
        </div>
      )}

      </fieldset>

      <button
        data-testid="profile-save-button"
        type="button"
        disabled={saving || busy}
        onClick={save}
        className="btn btn-primary mt-6 w-full justify-center"
      >
        {saving ? "Saving profile…" : "Save candidate profile"}
      </button>

      {message && (
        <p
          className="mt-3 text-sm text-slate-800"
          data-testid="profile-saved-message"
          role="status"
        >
          {message}
        </p>
      )}

      <p className="mt-3 text-xs text-slate-500">
        Profile information is visible to authorised EuropEdu staff. Employer
        and partner access remains controlled by recorded consent.
      </p>
    </section>
  );
}
